// PhoneField — ISD code selector + local number input.
// India (+91) is pre-selected. Validates to exactly `isd.digits` digits.
// Outputs full E.164-style string via onChange: "+919876543210".

import { useState, useId, type JSX } from "react";
import { ISD_CODES, DEFAULT_ISD, parsePhone, formatPhone, type IsdCode } from "../lib/isd-codes.js";

interface Props {
  label:     string;
  value:     string;           // full stored phone e.g. "+919876543210"
  onChange:  (v: string) => void;
  required?: boolean;
  error?:    string;
}

// eslint-disable-next-line complexity -- many independent conditional branches; per-branch extraction would split into co-equal helpers without semantic gain. Tracked under TASK-N19 Phase B.
export function PhoneField({ label, value, onChange, required, error }: Props): JSX.Element {
  const id = useId();
  const parsed = value ? parsePhone(value) : { isd: DEFAULT_ISD, local: "" };
  const [isd, setIsd] = useState<IsdCode>(parsed.isd);
  const [local, setLocal] = useState(parsed.local);

  function handleIsdChange(code: string): void {
    const found = ISD_CODES.find((c) => c.code === code) ?? DEFAULT_ISD;
    setIsd(found);
    onChange(local ? formatPhone(found, local) : "");
  }

  function handleLocalChange(raw: string): void {
    const digits = raw.replace(/\D/g, "").slice(0, isd.digits);
    setLocal(digits);
    onChange(digits ? formatPhone(isd, digits) : "");
  }

  const isValid = !local || local.length === isd.digits;

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-slate-700 mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <div className="flex gap-2">
        {/* ISD dropdown */}
        <select
          value={isd.code}
          onChange={(e) => handleIsdChange(e.target.value)}
          className="shrink-0 rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-navy-500"
          aria-label="Country code"
        >
          {ISD_CODES.map((c, i) => (
            <option key={`${c.code}-${i}`} value={c.code}>
              {c.flag} {c.code} {c.country}
            </option>
          ))}
        </select>
        {/* Local number */}
        <input
          id={id}
          type="tel"
          inputMode="numeric"
          value={local}
          onChange={(e) => handleLocalChange(e.target.value)}
          placeholder={"0".repeat(isd.digits)}
          maxLength={isd.digits}
          required={required}
          className={`flex-1 rounded-lg border px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-navy-500 ${
            error || (!isValid && local) ? "border-red-400 bg-red-50" : "border-slate-300"
          }`}
        />
      </div>
      {/* Digit count hint */}
      {local && !isValid && (
        <p className="mt-1 text-xs text-red-500">
          {isd.country} numbers must be exactly {isd.digits} digits ({local.length}/{isd.digits})
        </p>
      )}
      {error && isValid && (
        <p className="mt-1 text-xs text-red-500">{error}</p>
      )}
    </div>
  );
}
